<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div>
          <button @click="back" class="btn btn-sm btn-outline-secondary mb-2">
            <i class="mdi mdi-arrow-left mr-1"></i>{{ $t('common.back') }}
          </button>
          <h2 class="text-white font-weight-bold mb-1">{{ packageDetail.package_name || $t('superadmin.packagesTitle') }}</h2>
          <p class="text-muted small mb-0">{{ $t('superadmin.packagesSubtitle') }}</p>
        </div>
        <div class="mt-2 mt-md-0 d-flex gap-2">
          <router-link
            :to="{ name: 'EditPackage', params: { id: String(packageDetail.id) } }"
            class="btn btn-outline-warning btn-sm px-3"
          >
            <i class="mdi mdi-pencil mr-1"></i>{{ $t('common.edit') }}
          </router-link>
          <button
            @click="confirmDelete"
            class="btn btn-outline-danger btn-sm px-3 ml-2"
          >
            <i class="mdi mdi-delete-outline mr-1"></i>{{ $t('common.delete') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Package Detail Card -->
    <div class="col-lg-8 col-12 grid-margin">
      <div class="card border-0 shadow-sm" style="background: #191c24; border-radius: 12px;">
        <div class="card-body p-4">
          <div class="d-flex align-items-center mb-4">
            <div
              class="rounded-circle mr-3 d-flex align-items-center justify-content-center bg-dark text-warning"
              style="width: 50px; height: 50px; border: 1px solid rgba(255, 171, 0, 0.3);"
            >
              <i class="mdi mdi-cube-outline" style="font-size: 26px;"></i>
            </div>
            <div>
              <h3 class="text-white font-weight-bold mb-1">{{ packageDetail.package_name }}</h3>
              <span class="badge badge-success px-3 py-1 font-weight-bold">
                {{ formatRupiah(packageDetail.price_per_person_per_month) }} / {{ $currentLocale === 'id' ? 'kursi / bulan' : 'user / month' }}
              </span>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-borderless text-light mb-0">
              <tbody>
                <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);">
                  <td class="text-muted py-3 pl-0" style="width: 35%;">{{ $t('superadmin.packageName') }}</td>
                  <td class="font-weight-bold text-white py-3">{{ packageDetail.package_name }}</td>
                </tr>
                <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);">
                  <td class="text-muted py-3 pl-0">{{ $t('superadmin.pricePerSeatMonth') }}</td>
                  <td class="font-weight-bold text-success py-3">{{ formatRupiah(packageDetail.price_per_person_per_month) }}</td>
                </tr>
                <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);">
                  <td class="text-muted py-3 pl-0">{{ $t('superadmin.maxUserSeats') }}</td>
                  <td class="font-weight-bold text-warning py-3">
                    <i class="mdi mdi-account-group mr-1"></i>
                    {{ packageDetail.limit_user || 0 }} {{ $t('quota.seats') }}
                  </td>
                </tr>
                <tr>
                  <td class="text-muted py-3 pl-0">{{ $t('superadmin.packageDescription') }}</td>
                  <td class="text-light py-3" style="line-height: 1.6;">
                    {{ packageDetail.description || '-' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Side Info Box -->
    <div class="col-lg-4 col-12 grid-margin">
      <div class="card border-0 shadow-sm" style="background: #191c24; border-radius: 12px;">
        <div class="card-body p-4">
          <h5 class="text-white font-weight-bold mb-3 d-flex align-items-center">
            <i class="mdi mdi-information-outline text-info mr-2"></i>
            {{ $currentLocale === 'id' ? 'Ringkasan Tingkatan Paket' : 'Plan Tier Overview' }}
          </h5>
          <p class="text-muted small mb-3">
            {{ $currentLocale === 'id' ? 'Tingkatan langganan ini mengatur batas maksimum kursi karyawan serta tarif penagihan yang dihitung otomatis saat pembuatan faktur bulanan.' : 'This subscription tier governs the maximum employee roster limit and billing rate calculated automatically during monthly invoice generation.' }}
          </p>
          <div class="p-3 rounded" style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06);">
            <div class="text-muted small">{{ $currentLocale === 'id' ? 'Skala Perusahaan Sasaran' : 'Target Enterprise Scale' }}</div>
            <div class="text-white font-weight-bold mt-1">
              {{ $currentLocale === 'id' ? `Hingga ${packageDetail.limit_user || 0} Karyawan Aktif` : `Up to ${packageDetail.limit_user || 0} Active Employees` }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import { deletePackage, getPackageDetail } from '@/services/packageService';

export default {
  name: 'PackageDetailView',
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      packageDetail: {},
      loading: true,
    };
  },
  mounted() {
    this.fetchPackageDetail();
  },
  methods: {
    async fetchPackageDetail() {
      try {
        this.loading = true;
        const res = await getPackageDetail(this.id);
        this.packageDetail = res.data || res;
      } catch (err) {
        console.error('Error fetching package detail:', err);
      } finally {
        this.loading = false;
      }
    },
    back() {
      this.$router.go(-1);
    },
    formatRupiah(value) {
      if (!value) return 'Rp 0';
      return `Rp ${parseInt(value).toLocaleString('id-ID')}`;
    },
    confirmDelete() {
      Swal.fire({
        title: this.$t('common.delete'),
        text: this.$t('superadmin.confirmDeletePackage'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: this.$t('common.delete'),
        cancelButtonText: this.$t('common.cancel'),
      }).then((result) => {
        if (result.isConfirmed) {
          deletePackage(this.id).then(() => {
            Swal.fire({
              icon: 'success',
              title: this.$t('common.success'),
              text: this.$currentLocale === 'id' ? 'Paket berhasil dihapus.' : 'Package deleted successfully.',
              confirmButtonColor: '#3085d6',
            }).then(() => {
              this.$router.push({ name: 'ListPackage' });
            });
          });
        }
      });
    },
  },
};
</script>